import { ErrorBoundary } from '@sentry/react';
import { Stacked } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import { Suspense } from 'react';
import {
  hasRetrievedAllTeamAggsAtom,
  teamAggsCountAtom,
  teamAggsDataAtom,
  unwrappedTeamAggsCountAtom,
} from '@/atoms/queries/team/teamAggData';
import { teamMetricsAtom } from '@/atoms/queries/team/teamRadar';
import { unwrappedVisibleTableColumnsAtom } from '@/atoms/table';
import { RankDataPillLegend } from '@/components/radar/table/RankDataPillLegend';
import { useTableColumns } from '@/hooks/useTableColumns';
import { GeneralErrorBoundaryFallback } from '../errorBoundary/ErrorBoundaryFallback';
import { AggsTable } from './AggsTable';
import { AggsTableErrorFallback } from './AggsTableErrorFallback';
import { TablePagination } from './TablePagination';

const TeamAggsTableWithData = () => {
  const [{ data, isPending }] = useAtom(teamAggsDataAtom);
  const visibleColumns = useAtomValue(unwrappedVisibleTableColumnsAtom);
  const hasRetrievedAllTeamAggs = useAtomValue(hasRetrievedAllTeamAggsAtom);
  const teamMetricData = useAtomValue(teamMetricsAtom);
  const totalCount = useAtomValue(unwrappedTeamAggsCountAtom);
  useTableColumns(['team', 'team_metric', 'team_total_metric']);

  return (
    <AggsTable
      data={data}
      isLoading={isPending}
      visibleColumns={visibleColumns}
      entity="team"
      hasRetrievedAllAggs={hasRetrievedAllTeamAggs}
      withVideo
      metricData={teamMetricData}
      totalCount={totalCount}
    />
  );
};

const TeamAggsTable = () => (
  <AggsTableErrorFallback>
    <Suspense fallback={<AggsTable isLoading entity="team" />}>
      <TeamAggsTableWithData />
    </Suspense>
  </AggsTableErrorFallback>
);

export const TeamAggsTableWithPagination = () => {
  const totalCount = useAtomValue(unwrappedTeamAggsCountAtom);

  return (
    <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
      <Stacked direction="column" gap="medium">
        <TeamAggsTable />
        <TablePagination dataLengthAtom={teamAggsCountAtom} />
        <RankDataPillLegend resultsCount={totalCount} />
      </Stacked>
    </ErrorBoundary>
  );
};
